import React from 'react';

export default function useSourcesSelection({
    activeViewType,
    currentFolderId,
    displayedSources,
}) {
    const [selectedKeys, setSelectedKeys] = React.useState(() => new Set());
    const [selectionAnchorKey, setSelectionAnchorKey] = React.useState(null);

    const selectableEntries = React.useMemo(() => {
        const folderEntries = (displayedSources.folders || [])
            .filter((folder) => activeViewType !== 'root' && !folder?.isPreset && !folder?.isSystem)
            .map((folder) => ({
                key: `folder:${String(folder.id)}`,
                itemType: 'folder',
                id: String(folder.id),
                parentFolderId: folder?.parentFolderId ?? null,
                name: folder?.name || 'Untitled folder',
            }));

        const fileEntries = (displayedSources.files || []).map((file) => ({
            key: `file:${String(file.id)}`,
            itemType: 'file',
            id: String(file.id),
            parentFolderId: file?.parentFolderId ?? currentFolderId ?? null,
            name: file?.name || 'Untitled file',
        }));

        return [...folderEntries, ...fileEntries];
    }, [activeViewType, currentFolderId, displayedSources.files, displayedSources.folders]);

    const selectableOrderKeys = React.useMemo(
        () => selectableEntries.map((entry) => entry.key),
        [selectableEntries],
    );

    const allowedSelectionKeys = React.useMemo(
        () => new Set(selectableOrderKeys),
        [selectableOrderKeys],
    );

    const selectedEntries = React.useMemo(() => (
        selectableEntries.filter((entry) => selectedKeys.has(entry.key) && allowedSelectionKeys.has(entry.key))
    ), [allowedSelectionKeys, selectableEntries, selectedKeys]);

    const clearSelection = React.useCallback(() => {
        setSelectedKeys(new Set());
        setSelectionAnchorKey(null);
    }, []);

    const toggleSelection = React.useCallback((event, entry) => {
        if (!entry?.key) {
            return;
        }

        const isShift = Boolean(event?.shiftKey);
        setSelectedKeys((current) => {
            const next = new Set(current);
            const currentIndex = selectableOrderKeys.indexOf(entry.key);
            const anchorIndex = selectionAnchorKey ? selectableOrderKeys.indexOf(selectionAnchorKey) : -1;

            if (isShift && currentIndex >= 0 && anchorIndex >= 0) {
                const from = Math.min(anchorIndex, currentIndex);
                const to = Math.max(anchorIndex, currentIndex);
                selectableOrderKeys.slice(from, to + 1).forEach((key) => next.add(key));
                return next;
            }

            if (next.has(entry.key)) {
                next.delete(entry.key);
            } else {
                next.add(entry.key);
            }
            return next;
        });

        if (!isShift) {
            setSelectionAnchorKey(entry.key);
        }
    }, [selectableOrderKeys, selectionAnchorKey]);

    const isFolderSelectable = React.useCallback(
        (folder) => activeViewType !== 'root' && !folder?.isPreset && !folder?.isSystem,
        [activeViewType],
    );

    const isFolderSelected = React.useCallback(
        (folder) => {
            const key = `folder:${String(folder?.id)}`;
            return selectedKeys.has(key) && allowedSelectionKeys.has(key);
        },
        [allowedSelectionKeys, selectedKeys],
    );

    const isFileSelected = React.useCallback(
        (file) => {
            const key = `file:${String(file?.id)}`;
            return selectedKeys.has(key) && allowedSelectionKeys.has(key);
        },
        [allowedSelectionKeys, selectedKeys],
    );

    const handleFolderSelectionToggle = React.useCallback((event, folder) => {
        if (!isFolderSelectable(folder)) {
            return;
        }

        toggleSelection(event, {
            key: `folder:${String(folder.id)}`,
            itemType: 'folder',
            id: String(folder.id),
            parentFolderId: folder?.parentFolderId ?? null,
            name: folder?.name || 'Untitled folder',
        });
    }, [isFolderSelectable, toggleSelection]);

    const handleFileSelectionToggle = React.useCallback((event, file) => {
        toggleSelection(event, {
            key: `file:${String(file.id)}`,
            itemType: 'file',
            id: String(file.id),
            parentFolderId: file?.parentFolderId ?? currentFolderId ?? null,
            name: file?.name || 'Untitled file',
        });
    }, [currentFolderId, toggleSelection]);

    return {
        selectedEntries,
        selectedCount: selectedEntries.length,
        clearSelection,
        isFolderSelectable,
        isFolderSelected,
        isFileSelected,
        handleFolderSelectionToggle,
        handleFileSelectionToggle,
    };
}
